/**
 * One fact's value where the memo shows it: in the header block or a
 * financial-summary cell.
 *
 * The states read differently on purpose, because each is a different claim:
 *  - found / derived: the value, underlined, opening its source;
 *  - a NUMERIC conflict: the value shown (the first source's) underlined in
 *    the conflict colour, and a click opens the conflict on the Gaps tab —
 *    where every source's value sits side by side and a person chooses. The
 *    other values are not printed beside it: "41.56 model: 39.99" in a cell
 *    read as two figures in the memo, and both are kept, never picked;
 *  - a TEXT fact with other readings: not a conflict. Wording differs between
 *    a deck and a filing without either being wrong, so the other readings sit
 *    under the value as one muted "also stated as" line, never flagged. Each
 *    reading is its own link to its source, and "+N" opens the rest
 *    (DOP-556) — a count nobody could open was a citation nobody could check;
 *  - a money figure the materials state in another currency: converted, it
 *    reads "$5.1mm (CAD 6.8mm)"; NOT converted (no rate and date in the
 *    materials), it keeps its own currency, "CAD 6.8mm", and is never dressed
 *    up with a "$";
 *  - manual: the value is a link like any other, in the colour of a
 *    person's own answer; it opens who entered it, when, where it came from
 *    and an Edit that reopens the answer (DOP-555, DOP-640) — a grey
 *    "entered by" line beside it read as a different kind of value. An
 *    entry that names a document (DOP-652) opens the document, as a found
 *    value does, and stays in the person's colour; who entered it is said
 *    when the pointer rests on it;
 *  - gap / not available: "Not found in provided materials", which IS the
 *    value, plus "blocks" while the fact's BLOCK GROUP is still open — one
 *    member found or entered satisfies the group, so a lone "blocks" on a
 *    field whose sibling was answered would be wrong. A not-available fact a
 *    PERSON accepted carries the same attribution as a manual value — the
 *    acceptance is a decision with a name and a date, not the engine's.
 */

import React, { useLayoutEffect, useRef, useState } from "react";

import type {
  DealScreeningFact,
  DealScreeningSource,
} from "../../types/dealScreening";
import {
  NOT_FOUND,
  formatFactValue,
  formatIsoDay,
  formatSourceLabel,
  formatTableValue,
  unconvertedCurrency,
} from "./dealScreeningFormat";
import { renderInline, stripMarks } from "./inlineMarks";
import DerivedFormulaPopover from "./DerivedFormulaPopover";
import { baseReading, spacedParts } from "./enteredDiff";
import { renderInlineSlice } from "./inlineMarks";
import PersonNote from "./PersonNote";

export interface FactValueProps {
  fact: DealScreeningFact;
  /** `text` for the header block ("$13.2mm"), `table` for a cell ("13.2"). */
  variant: "text" | "table";
  onOpenSource: (source: DealScreeningSource) => void;
  /** Whether a missing value says so in words, or as a dash in a dense row. */
  notFoundLabel?: string;
  /**
   * Every fact of the run by slot, so a value the run computed from other
   * facts (a discount to NAV, DOP-661) can open its arithmetic and inputs.
   */
  factsBySlot?: ReadonlyMap<string, DealScreeningFact>;
  /** The cover's tagline: its other wordings are kept for the diff, not listed. */
  hideOtherWordings?: boolean;
  /** The other readings open unfolded: a jump from Sources read lands on one. */
  expandOtherWordings?: boolean;
  /**
   * Whether this gap still blocks the memo. The caller knows the block
   * group; when absent, a blocking field's own gap is taken as open.
   */
  blocking?: boolean;
  /** Reopens a person's own answer. Offered only on facts a person answered. */
  onEdit?: (fact: DealScreeningFact) => void;
  /**
   * Opens an open conflict on the Gaps tab. Without it a conflicted value
   * opens its source like any other.
   */
  onOpenConflict?: (fact: DealScreeningFact) => void;
  /**
   * The company facts list (DOP-616): a long value stops at three lines
   * with "Show all", and other wordings fold behind one link.
   */
  compact?: boolean;
}

/** Whether a person answered this fact (entered a value or accepted N/A). */
const answeredByPerson = (fact: DealScreeningFact): boolean =>
  fact.state === "manual" ||
  (fact.state === "not_available" &&
    (fact.entered_by_user_id !== null ||
      fact.entered_by_name !== null ||
      fact.entered_at !== null));

/**
 * A person's answer, shown as a link like every sourced value (DOP-640). The
 * click opens where it came from, who entered it and when, and Edit — the
 * same questions a source link answers for an extracted value. The card is
 * the one every person's words and cells use (DOP-637).
 */
const EnteredValue: React.FC<{
  fact: DealScreeningFact;
  onEdit?: (fact: DealScreeningFact) => void;
  onOpenSource?: (source: DealScreeningSource) => void;
  children: React.ReactNode;
}> = ({ fact, onEdit, onOpenSource, children }) => {
  // Every source of the value in one card (Turtleback 10-03): the person's
  // entry, and what the materials themselves state for the same fact.
  const readings = onOpenSource
    ? fact.alternates.filter((alt) => alt.source.kind !== "user")
    : [];
  return (
    <PersonNote
      note={fact.note}
      by={fact.entered_by_name ?? "a person"}
      at={fact.entered_at}
      verb={fact.state === "manual" ? "Entered" : "Marked not found"}
      label={`Who entered ${fact.label}`}
      testId="fact-entered"
      attributionTestId="fact-attribution"
      footer={
        <>
          {readings.length > 0 && onOpenSource && (
            <div
              className="border-t border-[#f1f2f4] pt-1.5"
              data-testid="fact-entered-readings"
            >
              <p className="text-[11.5px] font-semibold text-[#3f4247]">
                The materials state
              </p>
              <ul className="mt-0.5 space-y-0.5">
                {readings.map((alt) => (
                  <li key={alt.source.anchor}>
                    <button
                      type="button"
                      onClick={() => onOpenSource(alt.source)}
                      title={`Open ${formatSourceLabel(alt.source)}`}
                      className="text-left text-[#004C4B] hover:underline"
                    >
                      <span className="line-clamp-2">
                        {typeof alt.value === "string"
                          ? stripMarks(alt.value)
                          : formatFactValue(
                              alt.value,
                              alt.unit || fact.unit,
                              unconvertedCurrency(alt),
                            )}
                      </span>
                      <span className="text-[#6B7280]">
                        {formatSourceLabel(alt.source)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {onEdit ? <EditFromCard fact={fact} onEdit={onEdit} /> : null}
        </>
      }
    >
      {children}
    </PersonNote>
  );
};

/** Edit, in the card: reopens the person's answer. */
const EditFromCard: React.FC<{
  fact: DealScreeningFact;
  onEdit: (fact: DealScreeningFact) => void;
}> = ({ fact, onEdit }) => (
  <button
    type="button"
    onClick={() => onEdit(fact)}
    className="text-[12px] font-medium text-[#008782] underline decoration-1 underline-offset-2 hover:text-[#004C4B]"
    data-testid="fact-edit"
  >
    Edit
  </button>
);

type Alternate = DealScreeningFact["alternates"][number];

/** "+N", folded: the compact list's form of AlsoStated. */
const OtherWordings: React.FC<{
  alternates: Alternate[];
  onOpenSource: (source: DealScreeningSource) => void;
  defaultOpen?: boolean;
}> = ({ alternates, onOpenSource, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <span
      className="mt-0.5 block font-sans text-[12px] leading-snug text-[#6B7280]"
      data-testid="fact-also-stated"
    >
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        title={`Also stated ${alternates.length} other way${alternates.length === 1 ? "" : "s"}`}
        className="text-[#004C4B] hover:underline"
        data-testid="fact-also-stated-more"
      >
        {open ? "Hide" : `+${alternates.length}`}
      </button>
      {open && (
        <ul
          className="mt-1 space-y-1 border-l-2 border-[#eef0f2] pl-2"
          data-testid="fact-also-stated-list"
          aria-label="Also read as, in the files"
        >
          {/* Every reading here is one the run read from a document, never a
              person's own wording (DOP-658): each names its page and opens it. */}
          {alternates.map((alt) => (
            <li key={alt.source.anchor}>
              <button
                type="button"
                onClick={() => onOpenSource(alt.source)}
                title={`Open ${formatSourceLabel(alt.source)}`}
                className="text-left hover:text-text-base hover:underline"
                data-testid="fact-also-stated-reading"
              >
                {String(alt.value)}
                <span className="text-[#9CA3AF]">
                  {" "}
                  · {formatSourceLabel(alt.source)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </span>
  );
};

/**
 * A text value, three lines at most until the reader asks for all. The
 * toggle is offered only when the text really runs past three lines
 * (DOP-659): it is measured, not guessed from a character count. `block`
 * beside `line-clamp-3` undid the clamp (the clamp needs its own display),
 * which is why "Show all" used to change nothing.
 */
const Clamped: React.FC<{ children: React.ReactNode; clamp: boolean }> = ({
  children,
  clamp,
}) => {
  const [all, setAll] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const box = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    if (!clamp || all) return undefined;
    const measure = () => {
      const el = box.current;
      if (el) setOverflows(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [clamp, all, children]);
  if (!clamp) return <>{children}</>;
  return (
    <span className="block">
      <span
        ref={box}
        className={all ? "block" : "line-clamp-3"}
        data-testid="fact-clamped"
      >
        {children}
      </span>
      {(overflows || all) && (
        <button
          type="button"
          onClick={() => setAll((was) => !was)}
          aria-expanded={all}
          className="font-sans text-[12px] text-[#004C4B] hover:underline"
        >
          {all ? "Show less" : "Show all"}
        </button>
      )}
    </span>
  );
};

/**
 * "also stated as: <reading> · +N": every reading a link to where it
 * was read, and the count a toggle that lists the others.
 */
const AlsoStated: React.FC<{
  alternates: Alternate[];
  onOpenSource: (source: DealScreeningSource) => void;
}> = ({ alternates, onOpenSource }) => {
  const [open, setOpen] = useState(false);
  const reading = (alt: Alternate) => (
    <button
      key={alt.source.anchor}
      type="button"
      onClick={() => onOpenSource(alt.source)}
      className="text-left hover:text-text-base hover:underline"
      data-testid="fact-also-stated-reading"
    >
      {String(alt.value)}
    </button>
  );
  const [first, ...rest] = alternates;
  return (
    <span
      className="mt-0.5 font-sans text-[12px] leading-snug text-[#6B7280]"
      data-testid="fact-also-stated"
    >
      also stated as: {reading(first)}
      {rest.length > 0 && (
        <>
          {" · "}
          <button
            type="button"
            onClick={() => setOpen((was) => !was)}
            aria-expanded={open}
            className="text-[#008782] hover:text-[#004C4B]"
            data-testid="fact-also-stated-more"
          >
            {open ? "show less" : `+${rest.length}`}
          </button>
          {open && (
            <ul
              className="mt-0.5 space-y-0.5"
              data-testid="fact-also-stated-list"
            >
              {rest.map((alt) => (
                <li key={alt.source.anchor}>{reading(alt)}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </span>
  );
};

const FactValue: React.FC<FactValueProps> = ({
  fact,
  variant,
  onOpenSource,
  notFoundLabel = NOT_FOUND,
  factsBySlot,
  hideOtherWordings = false,
  expandOtherWordings = false,
  blocking,
  onEdit,
  onOpenConflict,
  compact = false,
}) => {
  const format = variant === "text" ? formatFactValue : formatTableValue;
  const isText = fact.unit === "text";
  const base = answeredByPerson(fact) ? baseReading(fact) : null;
  // The reading an entry was built on is shown inside the value, not again
  // under "+N".
  const otherReadings = hideOtherWordings
    ? []
    : base
      ? fact.alternates.filter((alt) => alt !== base)
      : fact.alternates;
  const blocks = blocking ?? (fact.state === "gap" && fact.policy === "blocks");

  if (fact.state === "gap" || fact.state === "not_available") {
    if (answeredByPerson(fact)) {
      return (
        // The size of the value it stands in for (DOP-659): the words ARE the value.
        <span className="inline-flex flex-wrap items-baseline gap-x-1.5">
          <EnteredValue fact={fact} onEdit={onEdit} onOpenSource={onOpenSource}>
            {notFoundLabel}
          </EnteredValue>
        </span>
      );
    }
    return (
      <span className="inline-flex flex-wrap items-baseline gap-x-1.5">
        <span className="text-[#b54708]">
          {notFoundLabel}
          {fact.state === "gap" && blocks && (
            <span className="font-semibold"> · blocks</span>
          )}
        </span>
      </span>
    );
  }

  const converted = fact.original_currency !== null && fact.fx_rate !== null;
  // Words a person formatted (DOP-660) are drawn with their bold, italic
  // and underline; every other value reads as it always did.
  const shown =
    isText && typeof fact.value === "string"
      ? renderInline(fact.value)
      : variant === "text"
        ? formatFactValue(fact.value, fact.unit, unconvertedCurrency(fact))
        : format(fact.value, fact.unit, unconvertedCurrency(fact));
  const numericConflict = !isText && fact.conflict === "open";
  const openGap =
    numericConflict && onOpenConflict ? () => onOpenConflict(fact) : null;
  const document =
    fact.state === "manual" && fact.source && fact.source.kind !== "user"
      ? fact.source
      : null;
  const value = document ? (
    <button
      type="button"
      onClick={() => onOpenSource(document)}
      title={`Entered${fact.entered_by_name ? ` by ${fact.entered_by_name}` : ""}${
        fact.entered_at ? `, ${formatIsoDay(fact.entered_at)}` : ""
      } · Source: ${formatSourceLabel(document)}${fact.note ? ` · ${fact.note}` : ""}`}
      data-testid="fact-entered-document"
      className="text-left text-[#5B3FA8] underline decoration-[#a48ff0] decoration-1 underline-offset-2 hover:decoration-[#5B3FA8]"
    >
      {shown}
    </button>
  ) : answeredByPerson(fact) && base ? (
    // The person's entry shown as the reading it was built on, with only
    // their own words marked as theirs (DOP-658): a word added to a long
    // business model, or a bold put on two of its words, does not make the
    // whole value "entered by a person".
    <span data-testid="fact-entered-on-reading">
      {spacedParts(fact, base).map((part, index) => (
        // Each run of words is its own inline box, and a box drops the
        // space at its edge: the space is drawn between the boxes, or the
        // words on either side of a change join ("ofon-purpose").
        <React.Fragment key={index}>
          {part.spaceBefore && " "}
          {part.added ? (
            <EnteredValue fact={fact} onEdit={onEdit} onOpenSource={onOpenSource}>
              {renderInlineSlice(String(fact.value), part.from, part.to)}
            </EnteredValue>
          ) : (
            <button
              type="button"
              onClick={() => onOpenSource(base.source)}
              title={`Source: ${formatSourceLabel(base.source)}`}
              data-testid="fact-reading-kept"
              className="text-left underline decoration-1 decoration-[#004C4B]/50 underline-offset-2"
            >
              {renderInlineSlice(String(fact.value), part.from, part.to)}
            </button>
          )}
          {part.spaceAfter && " "}
        </React.Fragment>
      ))}
    </span>
  ) : answeredByPerson(fact) ? (
    <EnteredValue fact={fact} onEdit={onEdit} onOpenSource={onOpenSource}>
      {shown}
    </EnteredValue>
  ) : fact.source ? (
    <button
      type="button"
      onClick={openGap ?? (() => fact.source && onOpenSource(fact.source))}
      title={openGap ? "Two sources disagree — open the conflict" : undefined}
      data-testid={openGap ? "fact-conflict" : undefined}
      className={`text-left underline decoration-1 underline-offset-2 ${
        numericConflict
          ? "text-[#b54708] decoration-[#b54708]"
          : "decoration-[#004C4B]/50"
      }`}
    >
      {shown}
    </button>
  ) : // A value the run computed from other facts (a discount to NAV, DOP-661)
  // has no page to open: it opens the arithmetic and its inputs instead,
  // as a computed row of the Financial Summary does.
  fact.state === "derived" && fact.formula && factsBySlot ? (
    <span className="inline-flex items-baseline gap-1.5">
      <DerivedFormulaPopover
        fact={fact}
        factsBySlot={factsBySlot}
        variant="text"
      />
      <span className="font-sans text-[10px] text-text-muted">computed</span>
    </span>
  ) : (
    <span
      title={fact.state === "derived" ? (fact.note ?? undefined) : undefined}
    >
      {shown}
    </span>
  );

  if (compact) {
    return (
      <span className="block max-w-full">
        <Clamped clamp={isText}>{value}</Clamped>
        {isText && otherReadings.length > 0 && (
          <OtherWordings
            alternates={otherReadings}
            onOpenSource={onOpenSource}
            defaultOpen={expandOtherWordings}
          />
        )}
      </span>
    );
  }

  return (
    <span className="inline-flex max-w-full flex-col items-start">
      <span className="inline-flex flex-wrap items-baseline gap-x-1.5">
        {value}
        {converted && fact.original_value !== null && (
          <span className="font-sans text-[10px] text-text-muted">
            (
            {formatFactValue(
              fact.original_value,
              fact.unit,
              fact.original_currency,
            )}
            )
          </span>
        )}
      </span>
      {isText && otherReadings.length > 0 && (
        <AlsoStated alternates={otherReadings} onOpenSource={onOpenSource} />
      )}
    </span>
  );
};

export default FactValue;
